// @flow

/**
 * Feedback Service
 * Handles submission of user feedback to Langfuse
 */

import { LangfuseBrowserClient } from '@langfuse/browser';
import { LANGFUSE_PUBLIC_KEY, LANGFUSE_BASE_URL, AVL_ENVIRONMENT } from '../../../../config/vars';

// Initialize Langfuse browser client (lazy initialization)
let langfuseClient = null;

function getLangfuseClient() {
  if (!langfuseClient) {
    if (!LANGFUSE_PUBLIC_KEY) {
      console.warn('Langfuse public key not configured. Feedback will not be sent.');
      return null;
    }

    // Security: browser code holds the Langfuse public key only.
    // LangfuseBrowserClient accepts no secret key. Never construct a full
    // LangfuseClient here, as it takes a secret key and would expose it to
    // every user of the app.
    langfuseClient = new LangfuseBrowserClient({
      publicKey: LANGFUSE_PUBLIC_KEY,
      baseUrl: LANGFUSE_BASE_URL,
      environment: AVL_ENVIRONMENT || undefined,
    });
  }

  return langfuseClient;
}

/**
 * Submit feedback to Langfuse
 * @param {Object} params - Feedback parameters
 * @param {string} [params.traceId] - Langfuse trace ID to attach the score to (required)
 * @param {number|null} params.value - 1 for thumbs up, 0 for thumbs down, null to clear
 * @param {string} [params.comment] - Optional comment
 * @returns {Promise<void>}
 */
export async function submitFeedback({ traceId, value, comment, metadata }) {
  const client = getLangfuseClient();

  if (!client) {
    console.warn('Langfuse client not available. Feedback not sent.');
    return;
  }

  // New requirement: only ever send traceId as identifier
  if (!traceId) throw new Error('traceId is required to submit feedback');

  try {
    // If value is null, we're clearing the feedback
    // Langfuse doesn't have a delete API, so we'll just not send anything
    if (value === null) {
      console.log('Feedback cleared (not sent to Langfuse)');
      return;
    }

    await client.score({
      traceId,
      name: 'user-feedback',
      value,
      dataType: 'BOOLEAN',
      comment: comment || undefined,
      metadata: metadata || undefined,
    });

  } catch (error) {
    console.error('Failed to submit feedback to Langfuse:', error);
    throw new Error('Failed to submit feedback. Please try again.');
  }
}
